import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { 
  Camera, FileText, MessageSquare, CheckCircle, Users, TrendingUp,
  ArrowRight, Lightbulb, Clock, Shield, Star, Heart, Zap, Home,
  Euro, Target, Sparkles, ChevronRight, Award
} from 'lucide-react';

const LandlordTipsPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();

  const tips = [
    {
      icon: Camera,
      title: 'Investeer in goede foto\'s',
      description: 'Advertenties met professionele foto\'s krijgen 3x meer interesse. Zorg voor goede belichting, ruim de kamer op, en maak foto\'s overdag.',
      bonus: '📸 Tip: Gebruik je smartphone in landscape mode'
    },
    {
      icon: FileText,
      title: 'Schrijf een complete beschrijving',
      description: 'Vermeld alle belangrijke details: oppervlakte, voorzieningen, huisregels, en de buurt. Hoe completer, hoe meer relevante matches.',
      bonus: '✍️ Tip: Noem ook openbaar vervoer in de buurt'
    },
    {
      icon: MessageSquare,
      title: 'Reageer snel op matches',
      description: 'Wanneer iemand interesse toont, reageer binnen 24 uur. Snelle reacties leiden tot betere huurders en hogere conversie.',
      bonus: '⚡ Tip: Schakel notificaties in'
    },
    {
      icon: Shield,
      title: 'Bekijk profielen zorgvuldig',
      description: 'Swipe je woning toont je het volledige profiel: profieltype, taal, en hun persoonlijke bericht. Gebruik deze info voor je beslissing.',
      bonus: '🔍 Tip: Check hun beschrijving en voorkeuren'
    },
    {
      icon: Clock,
      title: 'Wees flexibel met bezichtigingen',
      description: 'Bied meerdere tijdslots aan. Weekenden en avonden werken vaak het beste voor werkende huurders.',
      bonus: '📅 Tip: Plan bezichtigingen in blokken'
    },
    {
      icon: Euro,
      title: 'Bepaal een eerlijke huurprijs',
      description: 'Onderzoek vergelijkbare woningen in je buurt. Een te hoge prijs betekent minder interesse, te laag wekt wantrouwen.',
      bonus: '💰 Tip: Check Pararius voor marktprijzen'
    }
  ];

  const stats = [
    { number: '3x', label: 'meer swipes met goede foto\'s', icon: Camera },
    { number: '47%', label: 'sneller verhuurd via ons', icon: Zap },
    { number: '<24u', label: 'ideale reactietijd', icon: Clock }
  ];

  const successStories = [
    {
      name: 'Henk van der Berg',
      role: 'Verhuurder in Amsterdam',
      quote: 'Binnen 5 dagen had ik 12 serieuze kandidaten. De kwaliteit van huurders via Swipe je woning is echt beter dan andere platforms.',
      avatar: 'H'
    },
    {
      name: 'Lisa de Vries',
      role: 'Verhuurder in Rotterdam',
      quote: 'Het matching-systeem werkt geweldig. Ik krijg alleen nog kandidaten die écht bij mijn woning passen.',
      avatar: 'L'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#F7F7F7]" data-testid="landlord-tips-page">
      <Header />
      
      <main className="flex-1 pt-20">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 px-4 overflow-hidden bg-gradient-to-br from-[#1F3A5F] via-[#2E7D32] to-[#1F3A5F]">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-10 left-[10%] w-72 h-72 bg-white rounded-full blur-[100px]"></div>
            <div className="absolute bottom-10 right-[10%] w-96 h-96 bg-[#2E7D32] rounded-full blur-[120px]"></div>
          </div>
          
          <div className="relative max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md rounded-full px-5 py-2.5 mb-6">
              <Lightbulb className="w-5 h-5 text-white" />
              <span className="text-white font-semibold text-sm">TIPS VOOR VERHUURDERS</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
              Vind sneller de
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-white to-[#90EE90]">
                perfecte huurder
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-10">
              Met deze bewezen tips verhuur je je woning sneller en aan betere huurders. 
              Onze verhuurders vinden gemiddeld 47% sneller een match.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/verhuurder/onboarding')}
                className="bg-white text-[#1F3A5F] hover:bg-white/90 h-14 px-8 text-lg font-bold rounded-full"
              >
                Start met verhuren
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-12 max-w-xl mx-auto">
              {stats.map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-3xl md:text-4xl font-black text-white">{stat.number}</div>
                  <div className="text-white/60 text-xs md:text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tips Section */}
        <section className="py-16 md:py-24 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-[#2E7D32]/10 text-[#2E7D32] px-4 py-2 rounded-full text-sm font-bold mb-4">
                <Target className="w-4 h-4" />
                BEWEZEN STRATEGIEËN
              </div>
              <h2 className="text-3xl md:text-4xl font-black text-[#2A2A2A] mb-4">
                6 tips om sneller te verhuren
              </h2>
              <p className="text-lg text-[#9E9E9E] max-w-2xl mx-auto">
                Deze tips zijn gebaseerd op data van duizenden succesvolle verhuurders
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tips.map((tip, i) => (
                <div 
                  key={i} 
                  className="bg-white rounded-2xl p-6 border border-[#E5E5E5] hover:shadow-xl hover:border-[#2E7D32]/30 transition-all hover:-translate-y-1"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-[#2E7D32] to-[#1F3A5F] rounded-2xl flex items-center justify-center mb-4">
                    <tip.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-[#2A2A2A] mb-2">{tip.title}</h3>
                  <p className="text-[#9E9E9E] text-sm leading-relaxed mb-4">{tip.description}</p>
                  <div className="bg-[#F7F7F7] rounded-xl p-3">
                    <p className="text-xs text-[#2A2A2A]">{tip.bonus}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Success Stories */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-[#1F3A5F]/10 text-[#1F3A5F] px-4 py-2 rounded-full text-sm font-bold mb-4">
                <Star className="w-4 h-4" />
                SUCCESVERHALEN
              </div>
              <h2 className="text-3xl md:text-4xl font-black text-[#2A2A2A]">
                Wat andere verhuurders zeggen
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {successStories.map((story, i) => (
                <div key={i} className="bg-[#F7F7F7] rounded-2xl p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#2E7D32] to-[#1F3A5F] rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {story.avatar}
                    </div>
                    <div>
                      <p className="font-bold text-[#2A2A2A]">{story.name}</p>
                      <p className="text-sm text-[#9E9E9E]">{story.role}</p>
                    </div>
                  </div>
                  <p className="text-[#2A2A2A] italic">&ldquo;{story.quote}&rdquo;</p>
                  <div className="flex items-center gap-1 mt-4">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Checklist CTA */}
        <section className="py-16 px-4 bg-[#F7F7F7]">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-[#1F3A5F] to-[#2E7D32] rounded-3xl p-8 md:p-12 text-white">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-1">
                  <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md rounded-full px-4 py-2 mb-4">
                    <Award className="w-4 h-4" />
                    <span className="text-sm font-semibold">GRATIS CHECKLIST</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-4">
                    Klaar om je woning te verhuren?
                  </h3>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-[#90EE90]" />
                      <span>Maak een aantrekkelijke advertentie</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-[#90EE90]" />
                      <span>Ontvang matches van gekwalificeerde huurders</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-[#90EE90]" />
                      <span>Chat direct met geïnteresseerden</span>
                    </li>
                  </ul>
                  <Button 
                    onClick={() => navigate('/verhuurder/onboarding')}
                    className="bg-white text-[#1F3A5F] hover:bg-white/90 h-14 px-8 rounded-full font-bold text-lg"
                  >
                    Plaats gratis je woning
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
                <div className="hidden md:block">
                  <div className="w-48 h-48 bg-white/10 rounded-3xl flex items-center justify-center">
                    <Home className="w-24 h-24 text-white/60" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-black text-[#2A2A2A] mb-4">
                Veelgestelde vragen
              </h2>
            </div>

            <div className="space-y-4">
              {[
                {
                  q: 'Hoe snel vind ik een huurder?',
                  a: 'Gemiddeld vinden verhuurders via Swipe je woning binnen 2 weken een geschikte huurder. Met goede foto\'s en een complete beschrijving kan dit nog sneller.'
                },
                {
                  q: 'Wat kost het om te verhuren via Swipe je woning?',
                  a: 'Het plaatsen van een advertentie is gratis. Je betaalt alleen wanneer je premium wilt voor extra features.'
                },
                {
                  q: 'Hoe selecteer ik de beste huurder?',
                  a: 'Je ziet direct het profiel, de voorkeuren en het persoonlijke bericht van elke geïnteresseerde. Zo kun je snel de beste match kiezen.'
                }
              ].map((faq, i) => (
                <div key={i} className="bg-[#F7F7F7] rounded-2xl p-6">
                  <h3 className="font-bold text-[#2A2A2A] mb-2">{faq.q}</h3>
                  <p className="text-[#9E9E9E]">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default LandlordTipsPage;
